import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import moment from 'moment';
import { toast } from 'react-toastify';

import InputWrapper from '../common/InputWrapper';
import Button from '../common/Button';
import Pagination from '../common/Pagination';

import { fetchGetPromocodes, fetchDeletePromocode } from '../../actions/settings';
import { showModal } from '../../actions/modal';

import { getCurrencySymbol, parseError } from '../../helpers';

import { PROMOCODES_COUNT_ON_PAGE } from '../../config';
import { useAutoTranslation } from '../../helpers/useAutoTranslation';

const Wrapper = styled('div')`
  min-height: 200px;
  .promocode-item {
    .promocode-name {
      font-weight: 600;
      cursor: pointer;
      .promocode-edit {
        visibility: hidden;
        margin-left: 10px;
        font-size: 16px;
        color: #98a6ad;
      }
    }

    .promocode-remove {
      display: none;
      cursor: pointer;
    }

    .badge {
      font-size: 12px;
      font-weight: 600;
    }

    &:hover {
      .promocode-edit {
        visibility: initial;
      }
      .promocode-remove {
        display: initial;
      }
    }
  }
`;

const PromocodeItem = (props) => {
  const { promocode, currency, handleEditPromocode, handleDeletePromocode, t } = props;

  let type = '—';
  let discount = '—';
  let validity = <span>—</span>;
  let status = { label: t('Активен'), style: 'success' };
  let findMulti = null;
  if (promocode.conditions && promocode.conditions.length) {
    const findTimeLimit = promocode.conditions.find((c) => c.type === 'TIME_LIMIT');
    const findUseLimit = promocode.conditions.find((c) => c.type === 'USE_LIMIT');
    findMulti = promocode.conditions.find((c) => c.type === 'MULTI');

    if (findTimeLimit) {
      const { fromDate, toDate } = findTimeLimit;
      if (fromDate && !toDate) {
        validity = <span>{`с ${moment(fromDate).format('DD.MM.YYYY')}`}</span>;
      }
      if (!fromDate && toDate) {
        validity = <span>{`${'до'} ${moment(toDate).format('DD.MM.YYYY')}`}</span>;
      }
      if (fromDate && toDate) {
        validity = (
          <span>
            {`${t('с')} ${moment(fromDate).format('DD.MM.YYYY')}`}
            <br />
            {`${'до'} ${moment(toDate).format('DD.MM.YYYY')}`}
          </span>
        );
      }

      if (toDate && moment().diff(moment(toDate), 'seconds') > 0) {
        status = { label: t('Срок истек'), style: 'danger' };
      }

      if (fromDate && moment().diff(moment(fromDate), 'seconds') < 0) {
        status = { label: t('Ожидание старта'), style: 'warning' };
      }
    }

    if (findUseLimit) {
      if (findUseLimit.maxNumberOfUse < promocode.useCount) {
        status = { label: t('Превышен лимит'), style: 'danger' };
      }
    }
  }

  if (promocode.discountType === 'FIX') {
    type = t('Фикс');
    discount = `${promocode.discountValue} ${getCurrencySymbol(currency)}`;
  }

  if (promocode.discountType === 'PERCENT') {
    type = t('Процент');
    discount = `${promocode.discountValue} %`;
  }

  if (findMulti) {
    type = t('Набор правил');
  }

  if (!promocode.isActive) {
    status = { label: t('Выключен'), style: 'secondary' };
  }

  return (
    <tr className="promocode-item">
      <td className="promocode-name" onClick={() => handleEditPromocode(promocode)}>
        {promocode.code}
        <span className="promocode-edit">
          <i className="fe-edit-2" style={{ lineHeight: `13px` }} />
        </span>
      </td>
      <td>{type}</td>
      <td>{discount}</td>
      <td>{validity}</td>
      <td>
        <span className={`badge badge-${status.style}`}>{status.label}</span>
      </td>
      <td style={{ width: '45px', height: '70px' }}>
        <div className="promocode-remove" onClick={() => handleDeletePromocode(promocode)}>
          <i className="fe-trash-2" />
        </div>
      </td>
    </tr>
  );
};

const PromocodesBlock = (props) => {
  const { checkoutSettings } = props;

  const { t } = useAutoTranslation();

  const [promocodes, setPromocodes] = useState([]);
  const [search, setSearch] = useState('');

  const [totalItems, setTotal] = useState(0);
  const [page, setPage] = useState(0);

  const getPromocodes = async (data = {}) => {
    const result = await dispatch(fetchGetPromocodes({ ...data, size: PROMOCODES_COUNT_ON_PAGE }));
    setPromocodes(result.promocodes);
    setTotal(result.recordsTotal);
  };

  useEffect(() => {
    (async function () {
      try {
        await getPromocodes();
      } catch (e) {}
    })();
  }, []);

  useEffect(() => {
    (async function () {
      const resObj = { page };
      if (search) {
        resObj.codeSearch = search;
        resObj.page = 0;
      }
      await getPromocodes(resObj);
    })();
  }, [search, page]);

  const dispatch = useDispatch();

  const handlePageSelect = (pageIndex) => {
    setPage(pageIndex);
  };

  const handleNewPromocode = () => {
    dispatch(
      showModal('EditPromocode', {
        submitCallback: () => {
          toast.info(t('Промокод успешно сохранен'));
          getPromocodes();
        },
      }),
    );
  };

  const handleEditPromocode = (currentPromocode) => {
    dispatch(
      showModal('EditPromocode', {
        currentCode: currentPromocode.code,
        submitCallback: () => {
          toast.info(t('Промокод успешно сохранен'));
          getPromocodes();
        },
      }),
    );
  };

  const handleDeletePromocode = (promocode) => {
    dispatch(
      showModal('Confirm', {
        body: `${t('Подтвердите удаление промокода')} "${promocode.code}"`,

        handleConfirm: async () => {
          await dispatch(fetchDeletePromocode(promocode.code)).catch((err) => {
            toast.error(parseError(err));
            throw err;
          });

          await getPromocodes();
          toast.info(t('Промокод успешно удален'));
        },

        confirmBtnTitle: t('Удалить'),
      }),
    );
  };

  return (
    <>
      <div className="border-bottom">
        <h4 className="header-title">{t('Промокоды')}</h4>
      </div>

      <div className="row promocodes-row" style={{ marginTop: '16px' }}>
        <div className="col-lg-4">
          <InputWrapper
            noLabel
            id="promocodeSearch"
            placeholder={t('Поиск: наименование')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="col-lg-8">
          <div className="text-lg-right">
            <Button
              classType="btn-danger"
              icon="fe-plus"
              text={t('Новый промокод')}
              onClick={handleNewPromocode}
            />
          </div>
        </div>
      </div>

      <Wrapper className="fixed-table-container">
        <div className="table-responsive">
          <table className="table table-centered table-hover">
            <thead className="thead-light">
              <tr>
                <th>{t('Промокод')}</th>
                <th>{t('Тип')}</th>
                <th>{t('Скидка')}</th>
                <th>{t('Срок действия')}</th>
                <th>{t('Статус')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {promocodes.map((p) => (
                <PromocodeItem
                  promocode={p}
                  currency={checkoutSettings.currency}
                  handleEditPromocode={handleEditPromocode}
                  handleDeletePromocode={handleDeletePromocode}
                  t={t}
                />
              ))}
            </tbody>
          </table>
          <Pagination
            total={totalItems}
            currentPage={Number(page)}
            perPage={PROMOCODES_COUNT_ON_PAGE}
            onPageSelect={handlePageSelect}
          />
        </div>
      </Wrapper>
    </>
  );
};

export default PromocodesBlock;
